import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";
import ConfirmDeleteModal from "../../components/confirmDeleteModal";

import { useToast } from "../../hooks/useToast";
import { useSelector } from "react-redux";
import { API_BASE } from "../../config/apiConfig";

import { FaEdit, FaTrash } from "react-icons/fa";

export default function CoachActivityDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem("access_token");

  const { showToast, ToastContainer } = useToast();

  const user = useSelector((state) => state.user.user); // for UI hide/show

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  // -------------------------------------------------------------------
  // LOAD ACTIVITY DETAIL
  // -------------------------------------------------------------------
  useEffect(() => {
    async function fetchDetail() {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}/api/coach-activities-detail/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = await res.json();

        if (!data.success) {
          showToast(data.message || "Failed to load activity", "error");
          navigate("/coach-activities");
          return;
        }

        setActivity(data.activity);
      } finally {
        setLoading(false);
      }
    }

    fetchDetail();
  }, [id, token]);

  // -------------------------------------------------------------------
  // DELETE
  // -------------------------------------------------------------------
  function handleDelete() {
    setDeleteId(id);
  }

  async function confirmDelete() {
    if (!deleteId) return;

    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/coach-activities-delete/${deleteId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();

      if (data.success) {
        navigate("/coach-activities");
      } else {
        showToast(data.message || "Delete failed", "error");
      }
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  }

  if (!activity) return <LoadingOverlay show={true} />;

  // -------------------------------------------------------------------
  // UI
  // -------------------------------------------------------------------
  return (
    <MainContentLayout>
      <LoadingOverlay show={loading} />
      <PageHeader title="Activity Detail" backTo="/coach-activities" />

      {/* Edit/Delete only visible if user is owner */}
      {activity.createdBy === user?.doc_id && (
        <div className="flex gap-3 justify-end px-3">
          {/* Edit */}
          <button
            onClick={() => navigate(`/coach-activities/edit/${id}`)}
            className="text-blue-600 hover:text-blue-800"
            title="Edit Activity"
          >
            <FaEdit size={18} />
          </button>

          {/* Delete */}
          <button
            onClick={handleDelete}
            className="text-red-600 hover:text-red-800"
            title="Delete Activity"
          >
            <FaTrash size={18} />
          </button>
        </div>
      )}

      {/* DETAILS */}
      <div className="p-5 mt-4 max-w-2xl mx-auto w-full text-left">
        <div className="grid grid-cols-2 gap-y-2">
          <span className="font-semibold">Name:</span>
          <span className="hyphens-auto">{activity.name}</span>

          <span className="font-semibold">Type:</span>
          <span>{activity.type}</span>

          {activity.type === "tournament" && (
            <>
              <span className="font-semibold">Ranking Type:</span>
              <span>{activity.rankingType || "-"}</span>
            </>
          )}

          <span className="font-semibold">Start Date:</span>
          <span>{activity.startDate}</span>

          <span className="font-semibold">Start Time:</span>
          <span>{activity.startTime || "-"}</span>

          <span className="font-semibold">End Date:</span>
          <span>{activity.endDate}</span>

          <span className="font-semibold">End Time:</span>
          <span>{activity.endTime || "-"}</span>

          <span className="font-semibold">Remarks:</span>
          <span className="hyphens-auto">
            {activity.remarks || "-"}
          </span>

          <span className="font-semibold">Status:</span>
          <span className="font-medium capitalize">{activity.status}</span>
        </div>
      </div>

      {/* DELETE MODAL */}
      {deleteId && (
        <ConfirmDeleteModal
          show={true}
          title="Delete Activity"
          message="Are you sure you want to delete this activity?"
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
        />
      )}

      <ToastContainer />
    </MainContentLayout>
  );
}
